import { useFullPageSheet } from "@base/ui/FullPageSheet";
import { Button, TextField } from "@buildblockinc/buildblock-design-system";
import type { DailyLogForm } from "@features/write-daily-log/model/form";
import type { ProjectContractorItem } from "@features/write-daily-log/model/projectContractorsSchema";
import type { ProjectTradeItem } from "@features/write-daily-log/model/projectTradesSchema";
import { useParams } from "@tanstack/react-router";
import { X } from "lucide-react";
import { useState } from "react";
import { ContractorSearchSheet } from "./ContractorSearchSheet";
import { DecimalNumberField } from "./NumberField";
import { WorkforceSearchSheet } from "./WorkforceSearchSheet";

interface WorkforceStepProps {
  form: DailyLogForm;
}

export function WorkforceStep({ form }: WorkforceStepProps) {
  const { projectId } = useParams({ strict: false });
  const contractorSheet = useFullPageSheet();
  const workforceSheet = useFullPageSheet();
  const [activeContractorIndex, setActiveContractorIndex] = useState<number | null>(null);

  return (
    <form.Field name="workforce_contractors" mode="array">
      {(contractorsField) => {
        const contractors = contractorsField.state.value;
        const activeWorkforces =
          activeContractorIndex !== null ? (contractors[activeContractorIndex]?.workforces ?? []) : [];

        return (
          <>
            <Button type="button" variant="outline" color="secondary" className="w-full" onClick={contractorSheet.open}>
              Add / Select Contractor
            </Button>

            <div className="flex flex-col gap-y-4 mt-4">
              {contractors.map((contractor, contractorIndex) => (
                <div
                  key={contractor.id}
                  className="flex flex-col gap-y-4 p-4 bg-white border border-blue-500 rounded-8"
                >
                  <p className="text-gray-900 text-subtitle2">{contractor.name}</p>

                  <form.Field name={`workforce_contractors[${contractorIndex}].workforces`} mode="array">
                    {(workforcesField) => (
                      <ul className="flex flex-col gap-y-2">
                        {(workforcesField.state.value ?? []).map((worker, workerIndex) => (
                          <li key={worker.id} className="flex gap-x-3 items-center p-4 bg-blue-10 rounded-8">
                            <span className="flex-1 min-w-0 text-gray-900 truncate text-label2">{worker.name}</span>
                            <form.Field
                              name={`workforce_contractors[${contractorIndex}].workforces[${workerIndex}].quantity`}
                            >
                              {(qtyField) => (
                                <TextField className="w-[55px]">
                                  <DecimalNumberField
                                    value={qtyField.state.value}
                                    onChange={qtyField.handleChange}
                                    placeholder="0"
                                  />
                                </TextField>
                              )}
                            </form.Field>
                            <button
                              type="button"
                              onClick={() =>
                                workforcesField.handleChange(
                                  workforcesField.state.value.filter((_, i) => i !== workerIndex),
                                )
                              }
                              aria-label={`Remove ${worker.name}`}
                            >
                              <X width={20} height={20} className="text-gray-500 cursor-pointer shrink-0" />
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                  </form.Field>

                  <Button
                    type="button"
                    variant="outline"
                    color="secondary"
                    className="w-full"
                    onClick={() => {
                      setActiveContractorIndex(contractorIndex);
                      workforceSheet.open();
                    }}
                  >
                    Add
                  </Button>
                </div>
              ))}
            </div>

            <ContractorSearchSheet
              projectId={projectId ?? ""}
              isOpen={contractorSheet.isOpen}
              onClose={contractorSheet.close}
              currentContractors={contractors.map((c) => ({ id: c.id, name: c.name }))}
              onPickCatalogItem={(item: ProjectContractorItem) => {
                if (contractorsField.state.value.some((c) => c.id === item.id)) return;
                contractorsField.handleChange([
                  ...contractorsField.state.value,
                  { id: item.id, name: item.name, workforces: [] },
                ]);
              }}
              onRemove={(id: number) => {
                contractorsField.handleChange(contractorsField.state.value.filter((c) => c.id !== id));
              }}
            />

            <WorkforceSearchSheet
              projectId={projectId ?? ""}
              isOpen={workforceSheet.isOpen}
              onClose={workforceSheet.close}
              currentWorkforces={activeWorkforces}
              onPickCatalogItem={(item: ProjectTradeItem) => {
                if (activeContractorIndex === null) return;
                const current = contractorsField.state.value;
                contractorsField.handleChange(
                  current.map((c, i) => {
                    if (i !== activeContractorIndex) return c;
                    if (c.workforces.some((w) => w.name === item.name)) return c;
                    return {
                      ...c,
                      workforces: [...c.workforces, { id: String(item.id), name: item.name, quantity: 0 }],
                    };
                  }),
                );
              }}
              onRemove={(id: string) => {
                if (activeContractorIndex === null) return;
                const current = contractorsField.state.value;
                contractorsField.handleChange(
                  current.map((c, i) => {
                    if (i !== activeContractorIndex) return c;
                    return { ...c, workforces: c.workforces.filter((w) => w.id !== id) };
                  }),
                );
              }}
            />
          </>
        );
      }}
    </form.Field>
  );
}
